import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import ProductManagement from '../../components/ProductManagement';
import ChangeLog from '../../../components/ChangeLog';
import MediaSelector from '../../../components/MediaSelector';
import AdminPagination from '../../components/AdminPagination';
import './Stores.css';

const PAGINATION_MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const Stores = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStore, setEditingStore] = useState(null);
  const [managingProducts, setManagingProducts] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingDiscountIndex, setEditingDiscountIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);
  const [storeCategories, setStoreCategories] = useState([]);
  const [discountForm, setDiscountForm] = useState({
    name: '',
    description: '',
    type: 'percentage',
    value: 0,
    minOrderAmount: 0,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: null,
    isActive: true,
    applyTo: 'order',
    itemIds: []
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categories: [],
    image: '',
    banners: [],
    pricingRange: '€€',
    deliveryFee: 0,
    maxDeliveryDistance: 20,
    deliveryTime: '',
    minOrderPrice: 0,
    phoneNumber: '',
    contactInformation: '',
    openingHours: {
      monday: { openingTime: '09:00', closingTime: '22:00', isOpen: true },
      tuesday: { openingTime: '09:00', closingTime: '22:00', isOpen: true },
      wednesday: { openingTime: '09:00', closingTime: '22:00', isOpen: true },
      thursday: { openingTime: '09:00', closingTime: '22:00', isOpen: true },
      friday: { openingTime: '09:00', closingTime: '22:00', isOpen: true },
      saturday: { openingTime: '09:00', closingTime: '22:00', isOpen: true },
      sunday: { openingTime: '09:00', closingTime: '22:00', isOpen: true }
    },
    deliveryMethods: {
      delivery: true,
      pickup: true,
      eatIn: false
    },
    hidden: false,
    closed: false,
    settings: {
      emailNotifications: {
        newOrder: true,
        orderCancelled: true,
        orderStatusChanged: true,
        reviewReceived: true
      },
      paymentMethods: {
        cash: true,
        card: true,
        online: true
      },
      displayStarRatings: true,
      taxRate: 25.5,
      appFeePercentage: 19.79,
      socialMedia: {
        facebook: '',
        instagram: '',
        twitter: '',
        website: ''
      },
      discounts: []
    },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      coordinates: { lat: '', lng: '' }
    },
    owner: ''
  });

  useEffect(() => {
    fetchStores();
    if ((user?.role === 'main_admin' || user?.role === 'admin')) {
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings/categories`);
        const raw = res.data.storeCategories || [];
        setStoreCategories(Array.isArray(raw) ? raw.map((c) => typeof c === 'string' ? { fi: c, en: c } : { fi: c?.fi ?? '', en: c?.en ?? '' }) : []);
      } catch (e) {
        console.error('Error fetching store categories:', e);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${PAGINATION_MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const totalPages = Math.max(1, Math.ceil(stores.length / itemsPerPage));
  const paginatedStores = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return stores.slice(start, start + itemsPerPage);
  }, [stores, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [stores.length, itemsPerPage]);

  const fetchStores = async () => {
    try {
      let response;
      if (user?.role === 'store_owner' || user?.role === 'store_user') {
        response = await axios.get(`${API_URL}/store-owner/stores`);
      } else {
        response = await axios.get(`${API_URL}/admin/stores`);
      }
      setStores(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching stores:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name || '',
      description: store.description || '',
      categories: Array.isArray(store.categories) && store.categories.length
        ? store.categories
        : store.category ? [store.category] : [],
      image: store.image || '',
      banners: store.banners || [],
      pricingRange: store.pricingRange || '€€',
      deliveryFee: store.deliveryFee || 0,
      maxDeliveryDistance: store.maxDeliveryDistance || 20,
      deliveryTime: store.deliveryTime || '',
      minOrderPrice: store.minOrderPrice || 0,
      phoneNumber: store.phoneNumber || '',
      contactInformation: store.contactInformation || '',
      openingHours: {
        monday: store.openingHours?.monday || { openingTime: '09:00', closingTime: '22:00', isOpen: true },
        tuesday: store.openingHours?.tuesday || { openingTime: '09:00', closingTime: '22:00', isOpen: true },
        wednesday: store.openingHours?.wednesday || { openingTime: '09:00', closingTime: '22:00', isOpen: true },
        thursday: store.openingHours?.thursday || { openingTime: '09:00', closingTime: '22:00', isOpen: true },
        friday: store.openingHours?.friday || { openingTime: '09:00', closingTime: '22:00', isOpen: true },
        saturday: store.openingHours?.saturday || { openingTime: '09:00', closingTime: '22:00', isOpen: true },
        sunday: store.openingHours?.sunday || { openingTime: '09:00', closingTime: '22:00', isOpen: true }
      },
      deliveryMethods: {
        delivery: store.deliveryMethods?.delivery !== false,
        pickup: store.deliveryMethods?.pickup !== false,
        eatIn: store.deliveryMethods?.eatIn === true
      },
      hidden: store.hidden === true,
      closed: store.closed === true,
      settings: {
        emailNotifications: {
          newOrder: store.settings?.emailNotifications?.newOrder !== false,
          orderCancelled: store.settings?.emailNotifications?.orderCancelled !== false,
          orderStatusChanged: store.settings?.emailNotifications?.orderStatusChanged !== false,
          reviewReceived: store.settings?.emailNotifications?.reviewReceived !== false
        },
        paymentMethods: {
          cash: store.settings?.paymentMethods?.cash !== false,
          card: store.settings?.paymentMethods?.card !== false,
          online: store.settings?.paymentMethods?.online !== false
        },
        displayStarRatings: store.settings?.displayStarRatings !== false,
        taxRate: store.settings?.taxRate ?? 25.5,
        appFeePercentage: store.settings?.appFeePercentage ?? 19.79,
        socialMedia: {
          facebook: store.settings?.socialMedia?.facebook || '',
          instagram: store.settings?.socialMedia?.instagram || '',
          twitter: store.settings?.socialMedia?.twitter || '',
          website: store.settings?.socialMedia?.website || ''
        },
        discounts: store.settings?.discounts || []
      },
      address: {
        street: store.address?.street || '',
        city: store.address?.city || '',
        state: store.address?.state || '',
        zipCode: store.address?.zipCode || '',
        coordinates: {
          lat: store.address?.coordinates?.lat || '',
          lng: store.address?.coordinates?.lng || ''
        }
      },
      owner: store.owner?._id || store.owner || ''
    });
  };

  const handleSave = async () => {
    try {
      const dataToSend = {
        ...formData,
        address: {
          ...formData.address,
          coordinates: {
            lat: formData.address.coordinates.lat ? parseFloat(formData.address.coordinates.lat) : undefined,
            lng: formData.address.coordinates.lng ? parseFloat(formData.address.coordinates.lng) : undefined
          }
        }
      };
      
      if (editingStore._id) {
        if (user?.role === 'store_owner' || user?.role === 'store_user') {
          await axios.patch(`${API_URL}/store-owner/stores/${editingStore._id}`, dataToSend);
        } else {
          await axios.patch(`${API_URL}/admin/stores/${editingStore._id}`, dataToSend);
        }
      } else {
        // Only main admins can create new stores
        if ((user?.role === 'main_admin' || user?.role === 'admin')) {
          await axios.post(`${API_URL}/admin/stores`, dataToSend);
        } else {
          showError(t('admin.cannotCreateStore'));
          return;
        }
      }
      await fetchStores();
      setEditingStore(null);
      showSuccess(t('admin.storeSaved'));
    } catch (error) {
      console.error('Error saving store:', error);
      showError(t('admin.errorSavingStore') + ': ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.confirmDeleteStore'))) return;
    try {
      // Only main admins can delete stores
      if ((user?.role === 'main_admin' || user?.role === 'admin')) {
        await axios.delete(`${API_URL}/admin/stores/${id}`);
        await fetchStores();
      } else {
        showError(t('admin.cannotDeleteStore'));
      }
    } catch (error) {
      console.error('Error deleting store:', error);
      showError(t('admin.errorDeletingStore'));
    }
  };

  const handleProductUpdate = async () => {
    const updatedStores = await fetchStores();
    if (managingProducts && Array.isArray(updatedStores)) {
      const updated = updatedStores.find(s => s._id === managingProducts._id);
      if (updated) setManagingProducts(updated);
    }
    setRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="stores-container">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="stores-container">
      <div className="stores-header">
        <h2>{(user?.role === 'store_owner' || user?.role === 'store_user') ? t('admin.myStores') : t('admin.storesManagement')}</h2>
        {(user?.role === 'main_admin' || user?.role === 'admin') && (
          <button onClick={() => handleEdit({})} className="add-btn">
            {t('admin.addNewStore')}
          </button>
        )}
      </div>

      <div className="stores-table-wrapper">
        <table className="stores-table">
          <thead>
            <tr>
              <th>{t('admin.name')}</th>
              <th>{t('admin.category')}</th>
              <th>{t('admin.owner')}</th>
              <th>{t('admin.address')}</th>
              {user?.role === 'main_admin' && <th>{t('admin.deliveryFee')}</th>}
              <th>{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {stores.length === 0 ? (
              <tr>
                <td colSpan={user?.role === 'main_admin' ? 6 : 5} className="no-data">{t('admin.noStoresFound')}</td>
              </tr>
            ) : (
              paginatedStores.map(store => (
                <tr key={store._id}>
                  <td className="store-name" data-label={t('admin.name')}>
                    {store.name}
                    {store.hidden && (
                      <span className="hidden-badge" title={t('admin.hideFromMainApp') || 'Hidden from main app'}>
                        {t('admin.hidden') || 'Hidden'}
                      </span>
                    )}
                    {store.closed && (
                      <span className="closed-badge" title={t('admin.orderingPaused') || 'Ordering paused'}>
                        {t('admin.closed') || 'Closed'}
                      </span>
                    )}
                  </td>
                  <td data-label={t('admin.category')}>
                    <span className="category-badge">
                      {(store.categories && store.categories.length)
                        ? store.categories.join(', ')
                        : (store.category || '—')}
                    </span>
                  </td>
                  <td className="owner-name" data-label={t('admin.owner')}>
                    {store.owner ? 
                      `${store.owner.firstName || ''} ${store.owner.lastName || ''}`.trim() || store.owner.email || 'N/A'
                      : t('admin.noOwner')
                    }
                  </td>
                  <td className="address-cell" data-label={t('admin.address')}>
                    {store.address ? 
                      `${store.address.street || ''}, ${store.address.city || ''}`.trim() || 'N/A'
                      : 'N/A'
                    }
                  </td>
                  {user?.role === 'main_admin' && (
                    <td className="delivery-fee" data-label={t('admin.deliveryFee')}>€{store.deliveryFee?.toFixed(2) || '0.00'}</td>
                  )}
                  <td data-label={t('admin.actions')}>
                    <div className="action-buttons">
                      <button onClick={() => handleEdit(store)} className="edit-btn">{t('admin.edit')}</button>
                      <button onClick={() => setManagingProducts(store)} className="products-btn">{t('admin.manageProducts')}</button>
                      {(user?.role === 'main_admin' || user?.role === 'admin') && (
                        <button onClick={() => handleDelete(store._id)} className="delete-btn">{t('admin.delete')}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={stores.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />

      {editingStore !== null && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingStore._id ? t('admin.editStore') : t('admin.addStore')}</h2>
              <button className="close-btn" onClick={() => setEditingStore(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-section">
                <h3>{t('admin.basicInformation')}</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('admin.name')} *</label>
                    <input 
                      type="text" 
                      value={formData.name} 
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group categories-form-group">
                    <div className="categories-label-row">
                      <label>{t('admin.categories') || 'Categories'}</label>
                      <span className="categories-count" aria-live="polite">
                        {formData.categories.length > 0
                          ? t('admin.categoriesSelected', { count: formData.categories.length }) || `${formData.categories.length} selected`
                          : ''}
                      </span>
                    </div>
                    {storeCategories.length > 0 ? (
                      <>
                        <div className="categories-actions">
                          <button
                            type="button"
                            className="categories-action-btn"
                            onClick={() => setFormData({ ...formData, categories: storeCategories.map((c) => (c.fi || c.en || '').trim()).filter(Boolean) })}
                          >
                            {t('admin.selectAll') || 'Select all'}
                          </button>
                          <button
                            type="button"
                            className="categories-action-btn"
                            onClick={() => setFormData({ ...formData, categories: [] })}
                          >
                            {t('admin.clearSelection') || 'Clear'}
                          </button>
                        </div>
                        <div className="checkbox-list categories-checkbox-list" role="group" aria-label={t('admin.categories') || 'Categories'}>
                          {[...storeCategories]
                            .filter((c) => (c.fi || c.en || '').trim())
                            .sort((a, b) => (a.fi || a.en || '').localeCompare(b.fi || b.en || ''))
                            .map((cat) => {
                              const value = (cat.fi || cat.en || '').trim();
                              const isChecked = formData.categories.includes(value);
                              const label = [cat.fi, cat.en].filter(Boolean).join(' / ');
                              return (
                                <label
                                  key={value}
                                  className={`checkbox-item category-chip ${isChecked ? 'category-chip--selected' : ''}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFormData({ ...formData, categories: [...formData.categories, value] });
                                      } else {
                                        setFormData({ ...formData, categories: formData.categories.filter((c) => c !== value) });
                                      }
                                    }}
                                    aria-label={label}
                                  />
                                  <span className="category-chip-label">{label}</span>
                                </label>
                              );
                            })}
                        </div>
                      </>
                    ) : (
                      <p className="categories-empty">{t('admin.noCategoriesConfigured') || 'No categories configured. Add them in Website settings.'}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t('admin.description')}</label>
                    <textarea 
                      value={formData.description} 
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.pricingRange')}</label>
                    <select 
                      value={formData.pricingRange} 
                      onChange={(e) => setFormData({ ...formData, pricingRange: e.target.value })}
                    >
                      <option value="€">€</option>
                      <option value="€€">€€</option>
                      <option value="€€€">€€€</option>
                      <option value="€€€€">€€€€</option>
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>{t('admin.image')}</label>
                    <MediaSelector
                      onSelect={(url) => setFormData({ ...formData, image: url })}
                      currentValue={formData.image}
                      category="general"
                      storeId={editingStore?._id}
                    />
                    {formData.image && (
                      <input 
                        type="text" 
                        value={formData.image} 
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        placeholder="Or enter URL manually..."
                        style={{ marginTop: '10px' }}
                      />
                    )}
                  </div>
                  <div className="form-group full-width">
                    <label>{t('admin.banners')}</label>
                    <MediaSelector
                      onSelect={(urls) => setFormData({ ...formData, banners: urls })}
                      currentValue={formData.banners}
                      category="banner"
                      multiple={true}
                      storeId={editingStore?._id}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.phoneNumber')}</label>
                    <input 
                      type="text" 
                      value={formData.phoneNumber} 
                      onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>{t('admin.contactInformation')}</label>
                    {(user?.role === 'main_admin' || user?.role === 'admin') ? (
                      <textarea 
                        value={formData.contactInformation} 
                        onChange={(e) => setFormData({ ...formData, contactInformation: e.target.value })}
                        rows="3"
                        placeholder={t('admin.contactInformationPlaceholder')}
                      />
                    ) : (
                      <div className="read-only-field">
                        {formData.contactInformation || t('admin.noContactInformation')}
                      </div>
                    )}
                  </div>
                  {(user?.role === 'main_admin' || user?.role === 'admin') && (
                    <div className="form-group">
                      <label>{t('admin.owner')}</label>
                      <select 
                        value={formData.owner} 
                        onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                      >
                        <option value="">{t('admin.noOwner')}</option>
                        {users.map(user => (
                          <option key={user._id} value={user._id}>
                            {user.firstName} {user.lastName} ({user.email}) - {user.role}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h3>{t('admin.address')}</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>{t('admin.street')}</label>
                    <input 
                      type="text" 
                      value={formData.address.street} 
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, street: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.city')}</label>
                    <input 
                      type="text" 
                      value={formData.address.city} 
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, city: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.state')}</label>
                    <input 
                      type="text" 
                      value={formData.address.state} 
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, state: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.zipCode')}</label>
                    <input 
                      type="text" 
                      value={formData.address.zipCode} 
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { ...formData.address, zipCode: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.latitude')}</label>
                    <input 
                      type="number" 
                      step="any"
                      value={formData.address.coordinates.lat} 
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { 
                          ...formData.address, 
                          coordinates: { 
                            ...formData.address.coordinates, 
                            lat: e.target.value 
                          }
                        }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.longitude')}</label>
                    <input 
                      type="number" 
                      step="any"
                      value={formData.address.coordinates.lng} 
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        address: { 
                          ...formData.address, 
                          coordinates: { 
                            ...formData.address.coordinates, 
                            lng: e.target.value 
                          }
                        }
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>{t('admin.deliveryPricing')}</h3>
                <div className="form-grid">
                  {user?.role === 'main_admin' && (
                    <div className="form-group">
                      <label>{t('admin.deliveryFeeEuro')}</label>
                      <input 
                        type="number" 
                        step="any"
                        min="0"
                        value={formData.deliveryFee} 
                        onChange={(e) => setFormData({ ...formData, deliveryFee: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>{t('admin.maxDeliveryDistance')}</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="20"
                      value={formData.maxDeliveryDistance} 
                      onChange={(e) => setFormData({ ...formData, maxDeliveryDistance: parseFloat(e.target.value) || 20 })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.deliveryTime')}</label>
                    <input 
                      type="text" 
                      value={formData.deliveryTime} 
                      onChange={(e) => setFormData({ ...formData, deliveryTime: e.target.value })}
                      placeholder={t('admin.deliveryTimePlaceholderStore')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.minimumOrderPrice')}</label>
                    <input 
                      type="text"
                      inputMode="decimal"
                      value={formData.minOrderPrice}
                      onChange={(e) => setFormData({ ...formData, minOrderPrice: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={formData.hidden} 
                        onChange={(e) => setFormData({ ...formData, hidden: e.target.checked })}
                      />
                      {t('admin.hideFromMainApp') || 'Hide from main app'}
                    </label>
                  </div>
                  <div className="form-group checkbox-group">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={formData.closed} 
                        onChange={(e) => setFormData({ ...formData, closed: e.target.checked })}
                      />
                      {t('admin.closeStoreOrderingPaused') || 'Close store (ordering paused due to congestion)'}
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>{t('admin.openingHours')}</h3>
                <div className="opening-hours-grid">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <div key={day} className="day-hours-row">
                      <div className="day-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.openingHours[day]?.isOpen !== false}
                            onChange={(e) => setFormData({
                              ...formData,
                              openingHours: {
                                ...formData.openingHours,
                                [day]: {
                                  ...formData.openingHours[day],
                                  isOpen: e.target.checked,
                                  openingTime: formData.openingHours[day]?.openingTime || '09:00',
                                  closingTime: formData.openingHours[day]?.closingTime || '22:00'
                                }
                              }
                            })}
                          />
                          <span className="day-label">{t(`admin.${day}`)}</span>
                        </label>
                      </div>
                      {formData.openingHours[day]?.isOpen !== false && (
                        <>
                          <input
                            type="time"
                            value={formData.openingHours[day]?.openingTime || '09:00'}
                            onChange={(e) => setFormData({
                              ...formData,
                              openingHours: {
                                ...formData.openingHours,
                                [day]: {
                                  ...formData.openingHours[day],
                                  openingTime: e.target.value
                                }
                              }
                            })}
                            className="time-input"
                          />
                          <span className="time-separator">-</span>
                          <input
                            type="time"
                            value={formData.openingHours[day]?.closingTime || '22:00'}
                            onChange={(e) => setFormData({
                              ...formData,
                              openingHours: {
                                ...formData.openingHours,
                                [day]: {
                                  ...formData.openingHours[day],
                                  closingTime: e.target.value
                                }
                              }
                            })}
                            className="time-input"
                          />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <h3>{t('admin.settings')}</h3>
                
                <div className="settings-subsection">
                  <h4>{t('admin.deliveryMethods')}</h4>
                  <div className="checkbox-group">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={formData.deliveryMethods.delivery} 
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          deliveryMethods: { ...formData.deliveryMethods, delivery: e.target.checked }
                        })}
                      />
                      {t('admin.delivery')}
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={formData.deliveryMethods.pickup} 
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          deliveryMethods: { ...formData.deliveryMethods, pickup: e.target.checked }
                        })}
                      />
                      {t('admin.pickup')}
                    </label>
                    <label>
                      <input 
                        type="checkbox" 
                        checked={formData.deliveryMethods.eatIn} 
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          deliveryMethods: { ...formData.deliveryMethods, eatIn: e.target.checked }
                        })}
                      />
                      {t('admin.eatIn')}
                    </label>
                  </div>
                </div>

                <div className="settings-subsection">
                  <h4>{t('admin.emailNotifications')}</h4>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.emailNotifications.newOrder}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            emailNotifications: {
                              ...formData.settings.emailNotifications,
                              newOrder: e.target.checked
                            }
                          }
                        })}
                      />
                      {t('admin.newOrder')}
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.emailNotifications.orderCancelled}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            emailNotifications: {
                              ...formData.settings.emailNotifications,
                              orderCancelled: e.target.checked
                            }
                          }
                        })}
                      />
                      {t('admin.orderCancelled')}
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.emailNotifications.orderStatusChanged}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            emailNotifications: {
                              ...formData.settings.emailNotifications,
                              orderStatusChanged: e.target.checked
                            }
                          }
                        })}
                      />
                      {t('admin.orderStatusChanged')}
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.emailNotifications.reviewReceived}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            emailNotifications: {
                              ...formData.settings.emailNotifications,
                              reviewReceived: e.target.checked
                            }
                          }
                        })}
                      />
                      {t('admin.reviewReceived')}
                    </label>
                  </div>
                </div>

                <div className="settings-subsection">
                  <h4>{t('admin.paymentMethods')}</h4>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.paymentMethods.card}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            paymentMethods: {
                              ...formData.settings.paymentMethods,
                              card: e.target.checked
                            }
                          }
                        })}
                      />
                      {t('admin.card')}
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.paymentMethods.online}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            paymentMethods: {
                              ...formData.settings.paymentMethods,
                              online: e.target.checked
                            }
                          }
                        })}
                      />
                      {t('admin.online')}
                    </label>
                  </div>
                </div>

                <div className="settings-subsection">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.settings.displayStarRatings !== false}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: {
                          ...formData.settings,
                          displayStarRatings: e.target.checked
                        }
                      })}
                    />
                    {t('admin.displayStarRatings') || 'Display star ratings in app'}
                  </label>
                </div>

                {user?.role === 'main_admin' && (
                  <div className="settings-subsection">
                    <h4>{t('admin.taxAndFees')}</h4>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>{t('admin.taxRate')} (%)</label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          max="100"
                          value={formData.settings.taxRate}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: {
                              ...formData.settings,
                              taxRate: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('admin.appFeePercentage')} (%)</label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          max="100"
                          value={formData.settings.appFeePercentage}
                          onChange={(e) => setFormData({
                            ...formData,
                            settings: {
                              ...formData.settings,
                              appFeePercentage: parseFloat(e.target.value) || 0
                            }
                          })}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="settings-subsection">
                  <h4>{t('admin.socialMedia')}</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Facebook</label>
                      <input
                        type="url"
                        value={formData.settings.socialMedia.facebook}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            socialMedia: {
                              ...formData.settings.socialMedia,
                              facebook: e.target.value
                            }
                          }
                        })}
                        placeholder="https://facebook.com/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Instagram</label>
                      <input
                        type="url"
                        value={formData.settings.socialMedia.instagram}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            socialMedia: {
                              ...formData.settings.socialMedia,
                              instagram: e.target.value
                            }
                          }
                        })}
                        placeholder="https://instagram.com/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Twitter</label>
                      <input
                        type="url"
                        value={formData.settings.socialMedia.twitter}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            socialMedia: {
                              ...formData.settings.socialMedia,
                              twitter: e.target.value
                            }
                          }
                        })}
                        placeholder="https://twitter.com/..."
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('admin.website')}</label>
                      <input
                        type="url"
                        value={formData.settings.socialMedia.website}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: {
                            ...formData.settings,
                            socialMedia: {
                              ...formData.settings.socialMedia,
                              website: e.target.value
                            }
                          }
                        })}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>

                <div className="settings-subsection">
                  <h4>{t('admin.discounts')}</h4>
                  <div className="discounts-list">
                    {(formData.settings.discounts || []).map((discount, index) => (
                      <div key={index} className="discount-item">
                        <div className="discount-header">
                          <strong>{discount.name || t('admin.unnamedDiscount')}</strong>
                          <div className="discount-actions">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingDiscountIndex(index);
                                setDiscountForm({
                                  ...discount,
                                  validFrom: discount.validFrom ? new Date(discount.validFrom).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                  validUntil: discount.validUntil ? new Date(discount.validUntil).toISOString().split('T')[0] : null,
                                  applyTo: discount.applyTo || 'order',
                                  itemIds: Array.isArray(discount.itemIds) ? discount.itemIds : []
                                });
                              }}
                              className="btn-edit-discount"
                            >
                              {t('admin.edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const newDiscounts = formData.settings.discounts.filter((_, i) => i !== index);
                                setFormData({
                                  ...formData,
                                  settings: {
                                    ...formData.settings,
                                    discounts: newDiscounts
                                  }
                                });
                              }}
                              className="btn-remove-discount"
                            >
                              {t('admin.delete')}
                            </button>
                          </div>
                        </div>
                        <div className="discount-details">
                          <p>{discount.description || t('admin.noDescription')}</p>
                          <p><strong>{t('admin.value')}:</strong> {discount.type === 'percentage' ? `${discount.value}%` : `€${discount.value}`}</p>
                          <p><strong>{t('admin.applyTo')}:</strong> {discount.applyTo === 'products' && discount.itemIds?.length
                            ? t('admin.selectedProducts', { count: discount.itemIds.length })
                            : t('admin.fullOrder')}</p>
                          {discount.minOrderAmount > 0 && <p><strong>{t('admin.minOrder')}:</strong> €{discount.minOrderAmount}</p>}
                          <p><strong>{t('admin.status')}:</strong> {discount.isActive ? t('admin.active') : t('admin.inactive')}</p>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingDiscountIndex(-1);
                        setDiscountForm({
                          name: '',
                          description: '',
                          type: 'percentage',
                          value: 0,
                          minOrderAmount: 0,
                          validFrom: new Date().toISOString().split('T')[0],
                          validUntil: null,
                          isActive: true,
                          applyTo: 'order',
                          itemIds: []
                        });
                      }}
                      className="btn-add-discount"
                    >
                      + {t('admin.addDiscount')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setEditingStore(null)} className="cancel-btn">{t('admin.cancel')}</button>
              <button onClick={handleSave} className="save-btn">{t('admin.save')}</button>
            </div>
            
            {editingStore && editingStore._id && (
              <div className="changelog-section">
                <ChangeLog entityType="Store" entityId={editingStore._id} />
              </div>
            )}
          </div>
        </div>
      )}

      {managingProducts && (
        <ProductManagement
          store={managingProducts}
          onClose={() => setManagingProducts(null)}
          onUpdate={handleProductUpdate}
          key={refreshKey}
        />
      )}

      {editingDiscountIndex !== null && (
        <div className="modal-overlay" onClick={() => setEditingDiscountIndex(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingDiscountIndex >= 0 ? t('admin.editDiscount') : t('admin.addDiscount')}</h3>
              <button className="close-btn" onClick={() => setEditingDiscountIndex(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('admin.name')} *</label>
                <input
                  type="text"
                  value={discountForm.name}
                  onChange={(e) => setDiscountForm({ ...discountForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.description')}</label>
                <textarea
                  value={discountForm.description}
                  onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.applyTo')} *</label>
                <select
                  value={discountForm.applyTo}
                  onChange={(e) => setDiscountForm({ ...discountForm, applyTo: e.target.value, itemIds: e.target.value === 'order' ? [] : discountForm.itemIds })}
                >
                  <option value="order">{t('admin.fullOrder')}</option>
                  <option value="products">{t('admin.selectedProductsLabel')}</option>
                </select>
              </div>
              {discountForm.applyTo === 'products' && editingStore?.products?.length > 0 && (
                <div className="form-group">
                  <label>{t('admin.selectProducts')}</label>
                  <div className="discount-items-checklist">
                    {editingStore.products.map((item) => (
                      <label key={item._id || item.name} className="discount-item-checkbox">
                        <input
                          type="checkbox"
                          checked={discountForm.itemIds.includes(String(item._id))}
                          onChange={(e) => {
                            const id = String(item._id);
                            const next = e.target.checked
                              ? [...discountForm.itemIds, id]
                              : discountForm.itemIds.filter((i) => i !== id);
                            setDiscountForm({ ...discountForm, itemIds: next });
                          }}
                        />
                        <span>{item.name} — €{typeof item.price === 'number' ? item.price.toFixed(2) : item.price}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {discountForm.applyTo === 'products' && (!editingStore?.products?.length) && (
                <p className="form-hint">{t('admin.saveStoreFirstToAddProductDiscount')}</p>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admin.type')} *</label>
                  <select
                    value={discountForm.type}
                    onChange={(e) => setDiscountForm({ ...discountForm, type: e.target.value })}
                  >
                    <option value="percentage">{t('admin.percentage')}</option>
                    <option value="fixed">{t('admin.fixedAmount')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('admin.value')} *</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={discountForm.value}
                    onChange={(e) => setDiscountForm({ ...discountForm, value: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('admin.minimumOrderAmount')}</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={discountForm.minOrderAmount}
                  onChange={(e) => setDiscountForm({ ...discountForm, minOrderAmount: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admin.validFrom')}</label>
                  <input
                    type="date"
                    value={discountForm.validFrom}
                    onChange={(e) => setDiscountForm({ ...discountForm, validFrom: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.validUntil')} ({t('admin.optional')})</label>
                  <input
                    type="date"
                    value={discountForm.validUntil || ''}
                    onChange={(e) => setDiscountForm({ ...discountForm, validUntil: e.target.value || null })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={discountForm.isActive}
                    onChange={(e) => setDiscountForm({ ...discountForm, isActive: e.target.checked })}
                  />
                  {t('admin.active')}
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setEditingDiscountIndex(null)} className="cancel-btn">{t('admin.cancel')}</button>
              <button
                onClick={() => {
                  const newDiscounts = [...(formData.settings.discounts || [])];
                  const payload = {
                    ...discountForm,
                    validFrom: discountForm.validFrom ? new Date(discountForm.validFrom) : null,
                    validUntil: discountForm.validUntil ? new Date(discountForm.validUntil) : null,
                    applyTo: discountForm.applyTo || 'order',
                    itemIds: Array.isArray(discountForm.itemIds) ? discountForm.itemIds : []
                  };
                  if (editingDiscountIndex >= 0) {
                    newDiscounts[editingDiscountIndex] = payload;
                  } else {
                    newDiscounts.push(payload);
                  }
                  setFormData({
                    ...formData,
                    settings: {
                      ...formData.settings,
                      discounts: newDiscounts
                    }
                  });
                  setEditingDiscountIndex(null);
                }}
                className="save-btn"
              >
                {t('admin.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stores;
